import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    TextField,
    Typography,
} from "@mui/material";
import amber from "@mui/material/colors/amber";
import green from "@mui/material/colors/green";
import { withStyles } from "@mui/styles";
import axios from "axios";
import * as React from "react";

import { RARITIES, unlockCurrency } from "../../utils/constants";
import { Helpers } from "../../utils/helpers";
import { PopupMessage } from "../../utils/message";

import { ChestData } from ".";
import {Config} from "../../config/config";

const graphicIdList = ["boss", "combat", "event", "family", "free", "smart", "special", "superior"];

interface NewBundleSate {
    modalVisible: boolean;
    isLoaded: boolean;
    creatingChest: boolean;
    error: null | string;
    form: any;
}

interface NewBundleProps {
    buttonText?: string;
    refreshParent(): void;
    classes: any;
    closeRowMenu(): void;
    chest: ChestData;
}

class EditChestWrapped extends React.Component<NewBundleProps, NewBundleSate> {

    constructor(props) {
        super(props);
        // List with grid items
        this.state = {
            // Flags
            modalVisible: false,
            isLoaded: false,
            error: null,
            creatingChest: false,
            // Form data
            form: {
                name: this.props.chest.name,
                graphic_id: this.props.chest.graphic_id,
                description: this.props.chest.description,
                currency: this.props.chest.currency,
                trackedValue: this.props.chest.tracked_value || "hq",
                price: this.props.chest.price,
                rarity: this.props.chest.rarity,
                localization_title: this.props.chest.localization_title,
                localization_desc: this.props.chest.localization_desc,
            },
        };
    }

    showModal = () => this.setState({ modalVisible: true });
    handleModalCancel = () => {
        this.props.closeRowMenu();
        this.setState({ modalVisible: false, error: null, form: {} });
    }
    handleModalCreate = () => {
        this.setState({ creatingChest: true }, async () => {
            try {
                const result = await axios.put(`${Config.getInstance.apiUrl}/api/chests`, {
                    ...this.state.form,
                    chest_id: this.props.chest.chest_id,
                }, {
                    headers: {
                        Authorization: Helpers.getSessionId(),
                    },
                });

                this.props.closeRowMenu();

                this.setState({ creatingChest: false, modalVisible: false, form: {} }, () => {
                    this.props.refreshParent();
                });
            } catch (error) {
                PopupMessage.error("Something went wrong");
                this.setState({ creatingChest: false });
            }
        });
    }

    handleChange = name => event => {
        const formData = { ...this.state.form };
        formData[name] = event.target.value;

        if (name === "currency" && event.target.value === 99) {
            formData.price = 0;
        }

        this.setState({ form: formData });
    }

    render() {
        const { classes } = this.props;

        const dialogContent = () => <div>
            <TextField
                margin="normal"
                id="chest_name"
                label="Chest name"
                fullWidth
                onChange={this.handleChange("name")}
                value={this.state.form.name || ""}
                size="small"
            />

            <TextField
                margin="normal"
                id="description"
                label="Description"
                fullWidth
                multiline
                maxRows="6"
                rows="3"
                onChange={this.handleChange("description")}
                value={this.state.form.description || ""}
                size="small"
            />

            <TextField
                select
                margin="normal"
                id="graphic_id"
                label="Graphic Id"
                fullWidth
                onChange={this.handleChange("graphic_id")}
                value={this.state.form.graphic_id || ""}
                helperText="ID of icon in the client"
                size="small"
            >
                {graphicIdList.map(graphic => (
                    <MenuItem key={graphic} value={graphic}>
                        {graphic}
                    </MenuItem>
                ))}
            </TextField>

            <TextField
                select
                margin="normal"
                id="rarity"
                label="Rarity"
                fullWidth
                onChange={this.handleChange("rarity")}
                value={this.state.form.rarity || ""}
                style={{ marginTop: 5 }}
                size="small"
            >
                {RARITIES.map(rarity => (
                    <MenuItem key={rarity.id} value={rarity.id}>
                        {rarity.label}
                    </MenuItem>
                ))}
            </TextField>

            <Typography variant="h6" style={{marginBottom: 0, marginTop: 15, color: "#919191"}}>
                Tracked Value
            </Typography>

            <TextField
                select
                margin="normal"
                id="trackedValue"
                fullWidth
                onChange={this.handleChange("trackedValue")}
                value={this.state.form.trackedValue || "hq"}
                style={{marginTop: 5}}
                size="small"
            >
                <MenuItem value="hq">
                    HQ Level
                </MenuItem>
                <MenuItem value="league">
                    League
                </MenuItem>
                <MenuItem value="rank">
                    Rank
                </MenuItem>
                <MenuItem value="vipLevel">
                    Vip Level
                </MenuItem>
                <MenuItem value="gemRollEvent">
                    Gem Event Rolls
                </MenuItem>
            </TextField>

            <Typography variant="h6" style={{ marginBottom: 0, marginTop: 15, color: "#919191" }}>
                Open condition
            </Typography>

            <TextField
                select
                margin="normal"
                id="currency"
                label="Currency"
                fullWidth
                onChange={this.handleChange("currency")}
                value={this.state.form.currency || ""}
                style={{ marginTop: 5 }}
                size="small"
            >
                {unlockCurrency.map(currency => (
                    <MenuItem key={currency.id} value={currency.id}>
                        {currency.label}
                    </MenuItem>
                ))}
            </TextField>

            {!!this.state.form.currency && this.state.form.currency !== 99 && <TextField
                margin="normal"
                id="price"
                label="Amount"
                type="number"
                fullWidth
                onChange={this.handleChange("price")}
                value={this.state.form.price || ""}
                size="small"
            />}

            <Typography variant="h6" style={{ marginBottom: 0, marginTop: 15, color: "#919191" }}>
                Localization
            </Typography>

            <TextField
                margin="normal"
                id="localization_title"
                label="Title"
                fullWidth
                onChange={this.handleChange("localization_title")}
                value={this.state.form.localization_title || ""}
                size="small"
            />

            <TextField
                margin="normal"
                id="localization_desc"
                label="Description"
                fullWidth
                onChange={this.handleChange("localization_desc")}
                value={this.state.form.localization_desc || ""}
                size="small"
            />
        </div>;

        return (
            <div>
                <MenuItem onClick={this.showModal}>
                    Edit
                </MenuItem>

                <Dialog
                    open={this.state.modalVisible}
                    onClose={this.handleModalCancel}
                    aria-labelledby="form-dialog-title"
                    fullWidth={true}
                    maxWidth="sm"
                >
                    <DialogTitle id="form-dialog-title">
                        Edit {this.props.chest.name}
                    </DialogTitle>
                    <DialogContent>
                        {dialogContent()}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            disableElevation
                            onClick={this.handleModalCancel}
                            disabled={this.state.creatingChest}
                            size="small"
                            variant="outlined"
                        >
                            Cancel
                        </Button>
                        <div className={classes.wrapper}>
                            <Button disableElevation
                                onClick={this.handleModalCreate}
                                color="primary"
                                disabled={this.state.creatingChest}
                                size="small"
                                variant="contained"
                            >
                                Edit
                            </Button>
                            {this.state.creatingChest && <CircularProgress size={24} className={classes.buttonProgress} />}
                        </div>

                    </DialogActions>
                </Dialog>
            </div>
        );
    }
}

const styles: any = theme => ({
    chips: {
        display: "flex",
        flexWrap: "wrap",
    },
    chip: {
        margin: 8 / 4,
    },
    formControl: {
        width: "100%",
        marginTop: 16,
        marginBottom: 8,
    },
    wrapper: {
        margin: 8,
        position: "relative",
    },
    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
    warning: {
        backgroundColor: amber[700],
    },
    message: {
        display: "flex",
        alignItems: "center",
    },
});

export const EditChest = withStyles(styles)(EditChestWrapped);
